// SCR-STAFF-936 — شناسنامه دستگاه: مشخصات، مکان و جابه‌جایی، وضعیت و از رده خارج کردن، شمارنده (US-ORG-030، 032، 035، 040، 041)؛ فاز 2
// (AP-ORG-03): گواهی‌ها (033)، قراردادها (034)، نرخ هزینه (042)، نگهداری (050، 051) و بهره‌وری (043)؛ گزارش خرابی از همین صفحه (COMP-REPORT-ISSUE).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type CounterReadingView, type DeviceLocationView, type DeviceView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  DetailPage,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  latinDigits,
  Money,
  ReasonDialog,
  StatusChip,
  useDisplaySettings,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { orgKeys, useBranchSelection } from './common';
import { DEVICES_FEATURE, deviceKeys, deviceTone, percentOf, ReportIssueButton, todayIn, zonedToUtcIso } from './deviceCommon';
import { CertificatesTab, ContractsTab, CostRatesTab, MaintenanceTab, UtilizationTab } from './DeviceMaintenanceTabs';
import { DeviceFormDialog } from './DevicesPage';

export const SCR_STAFF_936 = 'SCR-STAFF-936';

export function DeviceDetailPage() {
  const { t } = useTranslation('org');
  const { id = '' } = useParams();
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const write = useWriteAccess('org.device.manage');
  const [tab, setTab] = useState('profile');
  const [editing, setEditing] = useState(false);
  const [relocating, setRelocating] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const queryClient = useQueryClient();

  const device = useQuery({
    queryKey: deviceKeys.device(session?.tenantId, id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/devices/{id}', { params: { path: { id } } })),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: deviceKeys.devices(session?.tenantId) });

  const changeStatus = useMutation({
    mutationFn: (status: 'Active' | 'UnderMaintenance' | 'OutOfService') =>
      unwrap(tenantApi.POST('/api/v1/organization/devices/{id}:change-status', { params: { path: { id } }, body: { status, reason: null } })),
    onSuccess: refresh,
  });

  const data = device.data;
  const counterVisible = access.featureEnabled(DEVICES_FEATURE) && data !== undefined && data.counterUnit !== 'None' && data.counterUnit !== 'Session';
  const retired = data?.status === 'Retired';
  const devicesFeature = access.featureEnabled(DEVICES_FEATURE);
  const reports = devicesFeature && access.can('org.device.report.view');

  return (
    <>
    <DetailPage
      screenId={SCR_STAFF_936}
      titleKey="org:device_detail.title"
      titleValues={{ name: data?.name ?? '' }}
      isLoading={device.isPending}
      error={device.error}
      onRetry={() => void device.refetch()}
      activeTab={tab}
      onTabChange={setTab}
      badges={data ? <StatusChip tone={deviceTone(data.status)} labelKey={`org:device_status.${data.status}`} /> : undefined}
      actions={
        data && !retired ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <ReportIssueButton resourceKind="Device" resourceId={data.id} resourceName={data.name} onReported={refresh} />
            <Button onClick={() => setEditing(true)} disabled={!write.canWrite} data-testid="device-edit">
              {t('org:device_detail.edit')}
            </Button>
            <Button onClick={() => setRelocating(true)} disabled={!write.canWrite} data-testid="device-relocate">
              {t('org:device_detail.relocate')}
            </Button>
            {data.status === 'Active' ? (
              <Button color="warning" onClick={() => changeStatus.mutate('OutOfService')} disabled={!write.canWrite || changeStatus.isPending} data-testid="device-out-of-service">
                {t('org:device_detail.out_of_service')}
              </Button>
            ) : (
              <Button color="success" onClick={() => changeStatus.mutate('Active')} disabled={!write.canWrite || changeStatus.isPending} data-testid="device-activate">
                {t('org:device_detail.activate')}
              </Button>
            )}
            {(data.status === 'Active' || data.status === 'OutOfService') && (
              <Button color="error" onClick={() => setRetiring(true)} disabled={!write.canWrite} data-testid="device-retire">
                {t('org:device_detail.retire')}
              </Button>
            )}
          </Stack>
        ) : undefined
      }
      banner={
        <>
          {retired && data ? (
            <Alert severity="info" data-testid="device-retired-banner">
              {t('org:device_detail.retired_banner', { reason: data.retirementReason ?? '' })}
            </Alert>
          ) : null}
          {changeStatus.error ? <ErrorState error={changeStatus.error} compact /> : null}
        </>
      }
      tabs={[
        { id: 'profile', labelKey: 'org:device_detail.tabs.profile', content: data ? <ProfileTab device={data} /> : null },
        { id: 'history', labelKey: 'org:device_detail.tabs.history', content: <HistoryTab id={id} /> },
        ...(counterVisible && data ? [{ id: 'counter', labelKey: 'org:device_detail.tabs.counter', content: <CounterTab device={data} /> }] : []),
        ...(devicesFeature && data
          ? [
              { id: 'certificates', labelKey: 'org:device_detail.tabs.certificates', content: <CertificatesTab device={data} /> },
              { id: 'contracts', labelKey: 'org:device_detail.tabs.contracts', content: <ContractsTab device={data} /> },
              { id: 'maintenance', labelKey: 'org:device_detail.tabs.maintenance', content: <MaintenanceTab device={data} /> },
            ]
          : []),
        ...(reports && data
          ? [
              { id: 'cost-rates', labelKey: 'org:device_detail.tabs.cost_rates', content: <CostRatesTab device={data} /> },
              { id: 'utilization', labelKey: 'org:device_detail.tabs.utilization', content: <UtilizationTab device={data} /> },
            ]
          : []),
      ]}
    />
      {editing && data && <DeviceFormDialog branchId={data.branchId} device={data} onClose={() => setEditing(false)} />}
      {relocating && data && <RelocateDialog device={data} onClose={() => setRelocating(false)} />}
      {data && <RetireDialog open={retiring} device={data} onClose={() => setRetiring(false)} />}
    </>
  );
}

function ProfileTab({ device }: { device: DeviceView }) {
  const { t } = useTranslation('org');
  const rows: [string, ReactNode][] = [
    ['org:devices.form.code', <bdi key="code" dir="ltr">{device.code}</bdi>],
    ['org:devices.form.type', device.deviceTypeName ?? device.deviceTypeCode],
    ['org:devices.form.manufacturer', device.manufacturer],
    ['org:devices.form.model', device.model],
    ['org:devices.form.serial', <bdi key="serial" dir="ltr">{device.serialNumber}</bdi>],
    ['org:devices.form.room', device.roomName ?? t('common:states.none')],
    ['org:devices.form.is_bookable', device.isBookable ? t('org:rooms.bookable') : t('org:rooms.not_bookable')],
    ['org:devices.form.counter_unit', t(`org:counter_unit.${device.counterUnit}`)],
    ['org:devices.form.purchase_date', device.purchaseDate ? <JalaliDate key="purchase" value={device.purchaseDate} /> : t('common:states.none')],
    ['org:devices.form.purchase_price', device.purchasePrice ? <Money key="price" amountMinor={Number(device.purchasePrice.amount)} /> : t('common:states.none')],
    ['org:devices.form.supplier', device.supplierName ?? t('common:states.none')],
    ['org:devices.form.asset_number', device.assetNumber ?? t('common:states.none')],
  ];
  return (
    <Table size="small" aria-label={t('org:device_detail.tabs.profile')}>
      <TableBody>
        {rows.map(([key, value]) => (
          <TableRow key={key}>
            <TableCell component="th" scope="row" sx={{ width: 220 }}>
              {t(key)}
            </TableCell>
            <TableCell>{value}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function HistoryTab({ id }: { id: string }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const branches = useBranchSelection();
  const history = useQuery({
    queryKey: [...deviceKeys.device(session?.tenantId, id), 'history'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/devices/{id}/history', { params: { path: { id } } })),
  });
  const rooms = useQuery({
    queryKey: [...orgKeys.rooms(session?.tenantId), 'all-branches'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/rooms')),
  });
  const roomName = (roomId: string | null | undefined) => (roomId ? (rooms.data?.items.find((room) => room.id === roomId)?.name ?? '—') : t('common:states.none'));
  const branchName = (branchId: string | null | undefined) => (branchId ? (branches.nameOf(branchId) ?? '—') : t('common:states.none'));
  const columns: DataTableColumn<DeviceLocationView>[] = [
    { id: 'effective', headerKey: 'org:device_detail.history.effective', cell: (row) => <JalaliDate value={row.effectiveAt} withTime /> },
    { id: 'from', headerKey: 'org:device_detail.history.from', cell: (row) => `${branchName(row.fromBranchId)} / ${roomName(row.fromRoomId)}` },
    { id: 'to', headerKey: 'org:device_detail.history.to', cell: (row) => `${branchName(row.toBranchId)} / ${roomName(row.toRoomId)}` },
    { id: 'recorded', headerKey: 'org:device_detail.history.recorded', cell: (row) => <JalaliDate value={row.recordedAt} withTime /> },
  ];
  return (
    <Stack spacing={2}>
      <DataTable
        captionKey="org:device_detail.history.locations"
        columns={columns}
        rows={history.data?.locations}
        getRowId={(row) => row.id}
        isLoading={history.isPending}
        error={history.error}
        onRetry={() => void history.refetch()}
        emptyTitleKey="org:device_detail.history.empty"
      />
      <Typography variant="subtitle1" component="h3">
        {t('org:device_detail.history.statuses')}
      </Typography>
      <Table size="small" aria-label={t('org:device_detail.history.statuses')}>
        <TableBody>
          {(history.data?.statuses ?? []).map((row) => (
            <TableRow key={row.id}>
              <TableCell>
                <JalaliDate value={row.changedAt} withTime />
              </TableCell>
              <TableCell>{t(`org:device_status.${row.toStatus}`)}</TableCell>
              <TableCell>{row.reason ?? ''}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
}

function CounterTab({ device }: { device: DeviceView }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('org.device.usage.record');
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [value, setValue] = useState('');
  const [reset, setReset] = useState(false);
  const [note, setNote] = useState('');
  const counter = useQuery({
    queryKey: [...deviceKeys.device(session?.tenantId, device.id), 'counter'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/devices/{id}/counter', { params: { path: { id: device.id } } })),
  });
  const record = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/organization/devices/{id}/readings', {
          params: { path: { id: device.id } },
          body: { value: Number(latinDigits(value)), kind: reset ? 'Reset' : 'Periodic', note: note.trim() || null },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      setValue('');
      setNote('');
      setReset(false);
      void queryClient.invalidateQueries({ queryKey: deviceKeys.devices(session?.tenantId) });
    },
  });
  const readingColumns: DataTableColumn<CounterReadingView>[] = [
    { id: 'at', headerKey: 'org:counter.columns.at', cell: (row) => <JalaliDate value={row.readAt} withTime /> },
    { id: 'value', headerKey: 'org:counter.columns.value', cell: (row) => faDigits(Number(row.value)) },
    { id: 'kind', headerKey: 'org:counter.columns.kind', cell: (row) => t(`org:counter.kind.${row.kind}`) },
    { id: 'sessions', headerKey: 'org:counter.columns.sessions', cell: (row) => (row.sessionsQuantity === null || row.sessionsQuantity === undefined ? '—' : faDigits(Number(row.sessionsQuantity))) },
    {
      id: 'discrepancy',
      headerKey: 'org:counter.columns.discrepancy',
      cell: (row) =>
        row.discrepancyBasisPoints === null || row.discrepancyBasisPoints === undefined ? '—' : (
          <span data-testid={`reading-discrepancy-${row.id}`}>{t('org:counter.percent', { percent: percentOf(row.discrepancyBasisPoints) })}</span>
        ),
    },
  ];
  const digits = /^\d+$/.test(latinDigits(value));
  return (
    <Stack spacing={2}>
      <Typography data-testid="counter-last">
        {t('org:counter.last', { value: faDigits(Number(counter.data?.lastReading ?? device.lastReading ?? 0)), unit: t(`org:counter_unit.${device.counterUnit}`) })}
      </Typography>
      {(counter.data?.alerts ?? []).slice(0, 5).map((alert) => (
        <Alert key={alert.id} severity="warning" data-testid="counter-alert">
          {t(`org:counter.alert.${alert.kind}`, { percent: percentOf(alert.discrepancyBasisPoints), expected: faDigits(Number(alert.expectedQuantity)), measured: faDigits(Number(alert.measuredQuantity)) })}
        </Alert>
      ))}
      {write.canWrite && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
          <TextField size="small" label={t('org:counter.reading')} value={value} onChange={(event) => setValue(event.target.value)} slotProps={{ htmlInput: { inputMode: 'numeric', 'data-testid': 'counter-value' } }} />
          <TextField select size="small" label={t('org:counter.kind_label')} value={reset ? 'Reset' : 'Periodic'} onChange={(event) => setReset(event.target.value === 'Reset')} sx={{ minWidth: 180 }} slotProps={{ htmlInput: { 'data-testid': 'counter-kind' } }}>
            <MenuItem value="Periodic">{t('org:counter.kind.Periodic')}</MenuItem>
            <MenuItem value="Reset">{t('org:counter.kind.Reset')}</MenuItem>
          </TextField>
          {reset && <TextField size="small" required label={t('org:counter.reset_reason')} value={note} onChange={(event) => setNote(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'counter-note' } }} />}
          <Button variant="contained" onClick={() => record.mutate()} disabled={!digits || record.isPending || (reset && !note.trim())} data-testid="counter-record">
            {t('org:counter.record')}
          </Button>
        </Stack>
      )}
      {record.data?.alertRaised && (
        <Alert severity="warning" data-testid="counter-recorded-alert">
          {t('org:counter.recorded_alert', { percent: percentOf(record.data.discrepancyBasisPoints) })}
        </Alert>
      )}
      {record.error ? <ErrorState error={record.error} compact /> : null}
      <DataTable
        captionKey="org:counter.readings"
        columns={readingColumns}
        rows={counter.data?.readings}
        getRowId={(row) => row.id}
        isLoading={counter.isPending}
        error={counter.error}
        onRetry={() => void counter.refetch()}
        emptyTitleKey="org:counter.empty"
      />
    </Stack>
  );
}

function RelocateDialog({ device, onClose }: { device: DeviceView; onClose: () => void }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const branches = useBranchSelection();
  const queryClient = useQueryClient();
  const [branchId, setBranchId] = useState(device.branchId);
  const [roomId, setRoomId] = useState('');
  const crossBranch = branchId !== device.branchId;
  const rooms = useQuery({
    queryKey: [...orgKeys.rooms(session?.tenantId), branchId, 'relocate'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/rooms', { params: { query: { branchId, status: 'Active' } } })),
  });
  const impact = useQuery({
    queryKey: [...deviceKeys.device(session?.tenantId, device.id), 'relocation-impact'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/devices/{id}/relocation-impact', { params: { path: { id: device.id } } })),
    enabled: crossBranch,
  });
  const dates = useForm<{ effective: string | null }>({ defaultValues: { effective: todayIn(timeZone, 1) } });
  const effective = dates.watch('effective') ?? todayIn(timeZone, 1);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/organization/devices/{id}:relocate', {
          params: { path: { id: device.id } },
          body: { branchId, roomId: roomId || null, effectiveAt: crossBranch ? zonedToUtcIso(effective, '00:00', timeZone) : null },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.devices(session?.tenantId) });
      onClose();
    },
  });
  return (
    <FormDialog open screenId="SCR-STAFF-936-RELOCATE" titleKey="org:relocate.title" onSubmit={() => save.mutate()} onClose={onClose} isDirty={roomId !== ''} isSubmitting={save.isPending} submitError={save.error ?? undefined}>
      <TextField select fullWidth label={t('org:relocate.branch')} value={branchId} onChange={(event) => { setBranchId(event.target.value); setRoomId(''); }} slotProps={{ htmlInput: { 'data-testid': 'relocate-branch' } }}>
        {branches.options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField select fullWidth label={t('org:relocate.room')} value={roomId} onChange={(event) => setRoomId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'relocate-room' } }}>
        <MenuItem value="">{t('org:devices.form.room_none')}</MenuItem>
        {(rooms.data?.items ?? []).map((room) => (
          <MenuItem key={room.id} value={room.id}>
            {room.name}
          </MenuItem>
        ))}
      </TextField>
      {crossBranch && (
        <>
          <JalaliDatePickerRHF control={dates.control} name="effective" labelKey="org:relocate.effective" helperKey="org:relocate.effective_help" required />
          {impact.data && Number(impact.data.futureBookings) > 0 && (
            <Alert severity="warning" data-testid="relocate-impact">
              {t('org:relocate.impact', { n: faDigits(Number(impact.data.futureBookings)) })}
            </Alert>
          )}
        </>
      )}
    </FormDialog>
  );
}

function RetireDialog({ open, device, onClose }: { open: boolean; device: DeviceView; onClose: () => void }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const queryClient = useQueryClient();
  const [finalCounter, setFinalCounter] = useState('');
  const hasCounter = device.counterUnit !== 'None' && device.counterUnit !== 'Session' && device.lastReading !== null && device.lastReading !== undefined;
  const retire = useMutation({
    mutationFn: (reason: string) =>
      unwrap(
        tenantApi.POST('/api/v1/organization/devices/{id}:retire', {
          params: { path: { id: device.id } },
          body: { reason, retiredOn: todayIn(timeZone), finalCounter: hasCounter ? Number(latinDigits(finalCounter) || device.lastReading) : null },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.devices(session?.tenantId) });
      onClose();
    },
  });
  return (
    <ReasonDialog
      open={open}
      titleKey="org:retire.title"
      messageKey="org:retire.message"
      confirmKey="org:retire.confirm"
      busy={retire.isPending}
      onConfirm={(reason) => retire.mutate(reason)}
      onCancel={onClose}
      error={retire.error ?? undefined}
    >
      {hasCounter && (
        <TextField
          fullWidth
          label={t('org:retire.final_counter')}
          value={finalCounter}
          placeholder={faDigits(Number(device.lastReading))}
          onChange={(event) => setFinalCounter(event.target.value)}
          slotProps={{ htmlInput: { inputMode: 'numeric', 'data-testid': 'retire-final-counter' } }}
        />
      )}
    </ReasonDialog>
  );
}
